import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Wand2, Loader2, Coins, Volume2, VolumeX, Copy, Trash2, Play, Download, Clapperboard, X, ImagePlus,
} from "lucide-react";
import { CreditTopUp } from "@/components/CreditTopUp";

const PRESETS = [
  "Démo produit : montre-watch posée sur un bureau, rotation lumineuse, style pub",
  "Unboxing dynamique d'une mini enceinte Bluetooth, lumières néon",
  "Gros plan cosmétique : sérum doré versé, texture satinée, ambiance premium",
  "Gadget cuisine en action sur plan de travail, zoom produit, feed TikTok",
];

const RATIOS = [
  { key: "16:9", label: "Paysage", box: "w-8 h-[18px]", tid: "aspect-ratio-16-9" },
  { key: "9:16", label: "Portrait", box: "w-[18px] h-8", tid: "aspect-ratio-9-16" },
  { key: "1:1", label: "Carré", box: "w-6 h-6", tid: "aspect-ratio-1-1" },
];

const STATUS_STEPS = ["Envoi à Kling 3.0...", "Séquençage des frames...", "Rendu du mouvement...", "Rendu audio...", "Finalisation..."];

export default function Studio() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(5);
  const [ratio, setRatio] = useState("16:9");
  const [sound, setSound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gens, setGens] = useState([]);
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("kling-3.0");
  const [refImages, setRefImages] = useState([]);
  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const selModel = models.find((m) => m.id === model);
  const cps = selModel ? selModel.credits_per_second : 0;
  const dMin = selModel ? selModel.min_duration : 3;
  const dMax = selModel ? selModel.max_duration : 15;
  const baseCost = Math.ceil(cps * duration);
  const cost = baseCost + (sound ? 2 : 0);
  const anyProcessing = gens.some((g) => g.status === "processing");
  const hasActiveSub = user?.role === "admin" || !!user?.subscription_plan;

  useEffect(() => {
    api.get("/models").then(({ data }) => setModels(data.models)).catch(() => {});
  }, []);

  // clamp duration to the selected model's allowed range
  useEffect(() => {
    if (!selModel) return;
    setDuration((d) => Math.min(selModel.max_duration, Math.max(selModel.min_duration, d)));
  }, [model]); // eslint-disable-line

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const room = 6 - refImages.length;
    files.slice(0, room).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setRefImages((prev) => (prev.length >= 6 ? prev : [...prev, reader.result]));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeRef = (i) => setRefImages((prev) => prev.filter((_, idx) => idx !== i));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/videos");
      setGens(data);
    } catch (e) {}
  }, []);

  useEffect(() => { load(); }, [load]);

  // poll while any generation is processing
  useEffect(() => {
    if (anyProcessing) {
      pollRef.current = setInterval(load, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [anyProcessing, load]);

  // animated progress bar for the newest processing item
  useEffect(() => {
    if (!anyProcessing) { setProgress(0); setStepIdx(0); return; }
    const t = setInterval(() => {
      setProgress((p) => Math.min(95, p + Math.random() * 8));
      setStepIdx((s) => (s + 1) % STATUS_STEPS.length);
    }, 1400);
    return () => clearInterval(t);
  }, [anyProcessing]);

  useEffect(() => {
    if (!anyProcessing) return;
    // whenever list refreshes and something completed, refresh credits
    refresh();
  }, [gens.length]); // eslint-disable-line

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Écrivez un prompt d'abord."); return; }
    // client-side gate for UX; the server also enforces the paywall
    if (!hasActiveSub) { setShowPaywall(true); return; }
    setSubmitting(true);
    try {
      await api.post("/videos/generate", { prompt, duration, aspect_ratio: ratio, sound, model, reference_images: refImages });
      toast.success("Génération lancée !");
      setProgress(5);
      await load();
      await refresh();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403 && detail === "subscription_required") {
        setShowPaywall(true);
      } else if (status === 402) {
        toast.error("Crédits insuffisants — rechargez ou passez à un plan supérieur.");
        navigate("/pricing");
      } else {
        toast.error(formatApiError(detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/videos/${id}`);
    setGens((g) => g.filter((x) => x.id !== id));
    toast.success("Supprimée.");
  };

  const copyPrompt = (p) => { navigator.clipboard.writeText(p); toast.success("Prompt copié."); };

  return (
    <div className="studio-bg grain min-h-screen relative">
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-[400px_1fr] gap-8">
        {/* generator panel */}
        <div className="lg:sticky lg:top-24 h-fit fade-up">
          <div className="glass rounded-3xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <Clapperboard className="w-5 h-5 text-[#3ECF8E]" />
              <h2 className="font-display text-xl font-bold">Nouvelle génération</h2>
            </div>

            {/* model selector */}
            <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Modèle IA</label>
            <div className="grid grid-cols-2 gap-3 mt-2 mb-6">
              {models.map((m) => (
                <button key={m.id} onClick={() => setModel(m.id)}
                  className={`rounded-xl border py-3 px-3 text-left transition-all ${model === m.id ? "border-green-500 bg-green-500/15" : "border-white/10 bg-white/5 hover:border-white/20"}`}
                  data-testid={`model-${m.id}`}>
                  <div className="font-display text-sm font-bold">{m.name}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{m.credits_per_second} cr/s · {m.resolution || "HD"}</div>
                </button>
              ))}
            </div>

            <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Prompt</label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Décrivez la vidéo que vous imaginez..."
              className="mt-2 min-h-[120px] bg-white/5 border-white/10 resize-none text-[15px]"
              data-testid="prompt-input"
            />
            <div className="flex flex-wrap gap-2 mt-3">
              {PRESETS.map((p, i) => (
                <button key={i} onClick={() => setPrompt(p)}
                  className="text-xs text-slate-400 hover:text-green-300 border border-white/10 hover:border-green-500/40 rounded-full px-3 py-1.5 transition-colors"
                  data-testid={`preset-${i}`}>
                  {p.length > 32 ? p.slice(0, 32) + "…" : p}
                </button>
              ))}
            </div>

            {/* duration slider */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Durée</label>
                <span className="font-mono text-sm" data-testid="duration-value">
                  <span className="text-[#3ECF8E] font-bold">{duration}s</span>
                  <span className="text-slate-500"> · {baseCost} cr</span>
                </span>
              </div>
              <Slider
                className="mt-4"
                value={[duration]}
                min={dMin}
                max={dMax}
                step={1}
                onValueChange={(v) => setDuration(v[0])}
                data-testid="duration-slider"
              />
              <div className="flex justify-between text-[11px] text-slate-500 font-mono mt-1.5">
                <span>{dMin}s</span>
                <span>{dMax}s max</span>
              </div>
            </div>

            {/* ratio */}
            <div className="mt-6">
              <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Format</label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {RATIOS.map((r) => (
                  <button key={r.key} onClick={() => setRatio(r.key)}
                    className={`rounded-xl border py-3 flex flex-col items-center gap-2 transition-all ${ratio === r.key ? "border-green-500 bg-green-500/15" : "border-white/10 bg-white/5 hover:border-white/20"}`}
                    data-testid={r.tid}>
                    <div className={`${r.box} border-2 rounded ${ratio === r.key ? "border-green-300" : "border-slate-500"}`} />
                    <span className="text-[11px] text-slate-400">{r.key}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* sound */}
            <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
              <div className="flex items-center gap-2">
                {sound ? <Volume2 className="w-4 h-4 text-green-300" /> : <VolumeX className="w-4 h-4 text-slate-500" />}
                <span className="text-sm">Son {sound ? "activé" : "coupé"}</span>
              </div>
              <Switch checked={sound} onCheckedChange={setSound} data-testid="sound-toggle-switch" />
            </div>

            {/* reference images */}
            <div className="mt-6">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold tracking-wider uppercase text-slate-400">Images de référence</label>
                <span className="text-[11px] text-slate-500 font-mono">{refImages.length}/6</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Optionnel · guide l'IA avec le visuel de votre produit</p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {refImages.map((src, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10 group" data-testid={`ref-image-${i}`}>
                    <img src={src} alt={`ref ${i + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => removeRef(i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-ref-${i}`}>
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                {refImages.length < 6 && (
                  <button onClick={() => fileRef.current?.click()}
                    className="aspect-square rounded-lg border border-dashed border-white/15 hover:border-green-500/50 bg-white/5 grid place-items-center text-slate-500 hover:text-green-300 transition-colors"
                    data-testid="add-ref-image-button">
                    <ImagePlus className="w-5 h-5" />
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} data-testid="ref-image-input" />
            </div>

            <Button onClick={generate} disabled={submitting}
              className="w-full mt-6 h-12 bg-gradient-to-r from-[#3ECF8E] to-emerald-400 hover:from-[#2fb879] hover:to-emerald-300 text-black glow-btn rounded-full font-semibold text-base"
              data-testid="generate-video-button">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Wand2 className="w-5 h-5 mr-1" /> Générer la vidéo</>}
              <span className="ml-2 inline-flex items-center gap-1 text-xs bg-black/25 rounded-full px-2 py-0.5">
                <Coins className="w-3 h-3 text-lime-300" /> -{cost}
              </span>
            </Button>
          </div>

          <div className="mt-6">
            <CreditTopUp />
          </div>
        </div>

        {/* gallery */}
        <div className="fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Générations du projet</h2>
            <span className="text-sm text-slate-500 font-mono">{gens.length} clip{gens.length > 1 ? "s" : ""}</span>
          </div>

          {gens.length === 0 && (
            <div className="glass rounded-3xl p-16 text-center" data-testid="empty-gallery">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 grid place-items-center mx-auto mb-4">
                <Clapperboard className="w-8 h-8 text-[#3ECF8E]" />
              </div>
              <p className="text-slate-400">Aucune vidéo pour l'instant. Lancez votre première génération !</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="video-gallery-grid">
            {gens.map((g) => (
              <div key={g.id} className="glass rounded-2xl overflow-hidden group border border-white/10 hover:border-green-500/40 transition-colors" data-testid={`video-card-${g.id}`}>
                <div className="relative aspect-video bg-black/40 grid place-items-center">
                  {g.status === "processing" && (
                    <div className="w-full px-5" data-testid="video-generation-loading-state">
                      <div className="tracing-border h-1 rounded-full mb-3" style={{ width: `${Math.max(10, progress)}%` }} />
                      <div className="flex items-center gap-2 text-green-300 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="font-mono text-xs">{STATUS_STEPS[stepIdx]}</span>
                      </div>
                    </div>
                  )}
                  {g.status === "completed" && (
                    <>
                      <video src={g.video_url} className="w-full h-full object-cover" muted preload="metadata" />
                      <button onClick={() => setActive(g)} className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="video-card-play-button">
                        <div className="w-14 h-14 rounded-full bg-green-600/90 grid place-items-center glow-btn">
                          <Play className="w-6 h-6 text-white fill-white" />
                        </div>
                      </button>
                    </>
                  )}
                  {g.status === "failed" && (
                    <div className="text-center px-4">
                      <p className="text-sm text-red-400">Échec — crédits remboursés</p>
                      <p className="text-xs text-slate-500 mt-1">{g.error}</p>
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="text-[11px] font-mono bg-black/60 backdrop-blur rounded-md px-2 py-1 text-white/90">{g.aspect_ratio}</span>
                    <span className="text-[11px] font-mono bg-black/60 backdrop-blur rounded-md px-2 py-1 text-white/90">{g.duration}s</span>
                    {g.demo && <span className="text-[11px] font-mono bg-lime-400 rounded-md px-2 py-1 text-black">DÉMO</span>}
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-slate-300 line-clamp-2 min-h-[40px]">{g.prompt}</p>
                  <div className="flex items-center justify-between mt-3">
                    <button onClick={() => copyPrompt(g.prompt)} className="text-slate-400 hover:text-green-300 transition-colors" title="Copier le prompt" data-testid={`copy-${g.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-3">
                      {g.status === "completed" && (
                        <a href={g.video_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-green-300 transition-colors" title="Télécharger" data-testid={`download-${g.id}`}>
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => del(g.id)} className="text-slate-400 hover:text-red-400 transition-colors" title="Supprimer" data-testid={`delete-${g.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl bg-[#0c0c12] border-white/10 p-0 overflow-hidden">
          {active && (
            <div>
              <DialogTitle className="sr-only">Lecture de la vidéo générée</DialogTitle>
              <video src={active.video_url} controls autoPlay className="w-full max-h-[70vh] bg-black" data-testid="video-player" />
              <div className="p-5">
                <p className="text-sm text-slate-300">{active.prompt}</p>
                <div className="flex gap-2 mt-3 text-[11px] font-mono text-slate-500">
                  <span>{active.aspect_ratio}</span> · <span>{active.duration}s</span> · <span>{active.sound ? "son on" : "son off"}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPaywall} onOpenChange={setShowPaywall}>
        <DialogContent className="max-w-lg bg-[#0c0c12] border-white/10" data-testid="paywall-modal">
          <DialogTitle className="font-display text-2xl font-extrabold text-center">
            Abonnez-vous pour générer vos vidéos
          </DialogTitle>
          <p className="text-sm text-slate-400 text-center -mt-1">
            La génération de vidéos TikTok Shop est réservée aux membres. Choisissez une formule mensuelle :
          </p>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { name: "Starter", price: "19,99 €", credits: "200 cr." },
              { name: "Pro", price: "29,99 €", credits: "500 cr.", featured: true },
              { name: "Premium", price: "49,99 €", credits: "1200 cr." },
            ].map((p) => (
              <div key={p.name}
                className={`rounded-2xl border p-4 text-center ${p.featured ? "border-green-500/60 bg-green-500/10" : "border-white/10 bg-white/5"}`}
                data-testid={`paywall-plan-${p.name.toLowerCase()}`}>
                <div className="font-display font-bold">{p.name}</div>
                <div className="font-display text-lg font-extrabold mt-1">{p.price}</div>
                <div className="text-[11px] text-lime-300 font-mono mt-1">{p.credits}/mois</div>
              </div>
            ))}
          </div>
          <Button
            onClick={() => { setShowPaywall(false); navigate("/pricing"); }}
            className="w-full mt-4 h-12 bg-gradient-to-r from-[#3ECF8E] to-emerald-400 hover:from-[#2fb879] hover:to-emerald-300 text-black glow-btn rounded-full font-semibold text-base"
            data-testid="paywall-cta-button">
            Voir les tarifs & s'abonner
          </Button>
          <p className="text-xs text-slate-500 text-center">Annulable à tout moment · Paiement sécurisé PayPal</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
